import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  MapPin, 
  Calendar, 
  CheckCircle2, 
  Plane, 
  ArrowRight,
  ShieldCheck,
  ChevronDown,
  Star,
  Building2,
  PhoneCall
} from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { imageUrl } from '../lib/images';

export default function FeaturedTripsPage() {
  const { language, getLocalizedPath } = useLanguage();
  const isDa = language === 'da';

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedTripId, setExpandedTripId] = useState(null);

  // Fetch only trips marked with udvalgt_rejse = true in CMS
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetch('/_api/public/v2/content/rejser?limit=100')
      .then((res) => {
        if (!res.ok) throw new Error('CMS error');
        return res.json();
      })
      .then((data) => {
        if (isMounted && data?.items && Array.isArray(data.items)) {
          // Filter only items where udvalgt_rejse is true
          const featuredOnly = data.items.filter(
            (item) => item.udvalgt_rejse === true || item.udvalgt_rejse === 'true'
          );

          const normalized = featuredOnly.map((item) => ({
            id: item.id || item.hotel_id || (item.slug && (item.slug[language] || item.slug.da)),
            title: item.title,
            category: item.theme || item.badge,
            badge: item.badge,
            destination: item.destination,
            area: item.area,
            stars: typeof item.stars === 'number' ? item.stars : parseInt(item.stars, 10) || 0,
            image: Array.isArray(item.image) ? item.image : [item.image].filter(Boolean),
            short_desc: item.short_desc,
            description: item.description,
            price_from: item.price_from,
            period: item.period,
            highlights: item.highlights,
            hotel_id: item.hotel_id,
            udvalgt_rejse: item.udvalgt_rejse
          }));

          setTrips(normalized);
        }
      })
      .catch((err) => {
        console.warn('Could not fetch trips from CMS:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  const getText = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field.da || field.en || '';
  };

  // Build dynamic categories based on the destinations present in the featured trips
  const uniqueDestinations = Array.from(
    new Set(
      trips
        .map((t) => getText(t.destination))
        .filter(Boolean)
    )
  );

  const categories = [
    { id: 'all', label: isDa ? 'Alle udvalgte rejser' : 'All featured trips' },
    ...uniqueDestinations.map((dest) => ({
      id: dest.toLowerCase(),
      label: dest,
      destinationName: dest
    }))
  ];

  const filteredTrips = trips.filter((trip) => {
    if (activeCategory === 'all') return true;
    const dest = (getText(trip.destination) || '').toLowerCase();
    return dest === activeCategory;
  });

  const toggleExpand = (id) => {
    setExpandedTripId(expandedTripId === id ? null : id);
  };

  return (
    <div className="pt-24 md:pt-32 pb-20 min-h-screen bg-stone-50/50">
      <SEO
        title={isDa ? "Udvalgte Rejser – Håndplukkede Handicapvenlige Ferietilbud" : "Featured Trips – Handpicked Accessible Holiday Deals"}
        description={isDa 
          ? "Se vores udvalgte handicapvenlige rejser og populære hotelophold. Nøje udvalgt med fokus på tilgængelighed, tryghed og gode oplevelser."
          : "Explore our featured accessible holidays and popular hotel stays. Carefully selected with focus on accessibility, safety and memorable experiences."
        }
      />

      {/* Hero Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-10 md:mb-14">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#99A18C]/15 text-[#99A18C] text-xs md:text-sm font-semibold tracking-wide uppercase mb-4"
          >
            <Sparkles size={16} />
            {isDa ? 'Særligt udvalgt til dig' : 'Specially selected for you'}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-serif font-bold text-[#99A18C] mb-6 italic"
          >
            {isDa ? 'Udvalgte Rejser' : 'Featured Trips'}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-stone-600 text-base md:text-lg leading-relaxed mb-8"
          >
            {isDa
              ? 'Her finder du vores udvalgte rejser og særlige ferietilbud, som vi har håndplukket. Hver rejse bygger på personlig erfaring og grundig kvalitetssikring, så du altid er sikret fuld tilgængelighed, handicapvenlige værelser, liftbus og tryghed fra start til slut.'
              : 'Here you will find our featured holidays and special offers handpicked for you. Every trip builds upon personal inspection and thorough quality checks, guaranteeing wheelchair accessibility, barrier-free rooms, and complete peace of mind.'
            }
          </motion.p>

          {/* Dynamic Destination Filter Pills */}
          {categories.length > 2 && (
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-all cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-[#99A18C] text-white shadow-md shadow-[#99A18C]/20 scale-105'
                      : 'bg-white text-stone-600 border border-stone-200 hover:border-[#99A18C] hover:text-[#99A18C]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Loading state */}
      {loading && (
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="inline-block w-8 h-8 border-3 border-[#99A18C] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-stone-500 text-sm">
            {isDa ? 'Henter udvalgte rejser...' : 'Loading featured trips...'}
          </p>
        </div>
      )}

      {/* Empty State if no trips marked as featured */}
      {!loading && trips.length === 0 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto mb-16 text-center">
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-stone-200/80 shadow-sm">
            <Building2 className="mx-auto mb-4 text-[#99A18C]" size={42} />
            <h2 className="text-2xl font-serif font-bold text-stone-800 mb-3">
              {isDa ? 'Nye udvalgte rejser er på vej' : 'New featured trips are on the way'}
            </h2>
            <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-8 max-w-xl mx-auto">
              {isDa
                ? 'Vi er i gang med at udvælge og kvalitetssikre de næste rejser. Du kan i mellemtiden gå på opdagelse i alle vores handicapvenlige hoteller eller kontakte os for et skræddersyet tilbud.'
                : 'We are curating and inspecting the next featured trips. In the meantime, browse our accessible hotels or contact us for a tailored itinerary.'
              }
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={getLocalizedPath('hotels', language)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#99A18C] hover:bg-[#8a917e] text-white font-bold py-3.5 px-6 rounded-2xl shadow-md transition-all text-sm"
              >
                <span>{isDa ? 'Se alle hoteller' : 'Browse all hotels'}</span>
                <ArrowRight size={15} />
              </Link>
              <Link
                to={getLocalizedPath('quote', language)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 font-medium py-3.5 px-6 rounded-2xl transition-all text-sm"
              >
                {isDa ? 'Indhent skræddersyet tilbud' : 'Request a custom quote'}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Trips Grid */}
      {!loading && filteredTrips.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            {filteredTrips.map((trip, idx) => {
              const isExpanded = expandedTripId === trip.id;
              const tripImg = trip.image?.[0] || 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/ceef4750-2ca2-43c1-b82b-e06ba062b589.jpeg';
              const tripTitle = getText(trip.title);
              const tripBadge = getText(trip.badge) || getText(trip.category);
              const tripDest = getText(trip.destination);
              const tripArea = getText(trip.area);
              const tripShortDesc = getText(trip.short_desc);
              const tripDesc = getText(trip.description);
              const tripPrice = getText(trip.price_from);
              const tripPeriod = getText(trip.period);
              const tripHighlights = getText(trip.highlights);
              const hotelUrl = trip.hotel_id ? `/${language}/hotel/${trip.hotel_id}` : null;

              return (
                <motion.article
                  key={trip.id}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    {/* Image Container */}
                    <div className="relative h-64 sm:h-80 w-full overflow-hidden group">
                      <img
                        src={imageUrl(tripImg, 'width=900,quality=85,format=auto')}
                        alt={tripTitle}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                      {/* Top Badges */}
                      <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                        {tripBadge ? (
                          <span className="bg-white/95 backdrop-blur-md text-[#99A18C] px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wide shadow-sm flex items-center gap-1.5">
                            <Sparkles size={13} className="text-[#99A18C]" />
                            {tripBadge}
                          </span>
                        ) : <div />}
                        {tripPrice && (
                          <span className="bg-[#99A18C] text-white px-3.5 py-1.5 rounded-full text-xs font-bold shadow-md">
                            {tripPrice}
                          </span>
                        )}
                      </div>

                      {/* Bottom overlay info */}
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <div className="flex items-center gap-2 text-xs text-white/90 font-medium mb-1 drop-shadow">
                          <MapPin size={14} className="text-[#99A18C]" />
                          <span>
                            {tripDest}{tripArea ? ` · ${tripArea}` : ''}
                          </span>
                          {trip.stars > 0 && (
                            <span className="inline-flex items-center gap-0.5 ml-1 text-amber-300">
                              {[...Array(trip.stars)].map((_, sIdx) => (
                                <Star key={sIdx} size={12} fill="currentColor" />
                              ))}
                            </span>
                          )}
                        </div>
                        <h2 className="text-xl sm:text-2xl font-serif font-bold text-white drop-shadow">
                          {tripTitle}
                        </h2>
                      </div>
                    </div>

                    {/* Body Content */}
                    <div className="p-6 sm:p-8">
                      {/* Key Details Bar */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-6 mb-6 border-b border-stone-100 text-xs text-stone-600">
                        {tripPeriod && (
                          <div className="flex items-center gap-2 bg-stone-50 px-3.5 py-2.5 rounded-xl border border-stone-100">
                            <Calendar size={16} className="text-[#99A18C] shrink-0" />
                            <span><strong>{isDa ? 'Periode:' : 'Period:'}</strong> {tripPeriod}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 bg-stone-50 px-3.5 py-2.5 rounded-xl border border-stone-100">
                          <ShieldCheck size={16} className="text-[#99A18C] shrink-0" />
                          <span><strong>{isDa ? 'Tryghed:' : 'Safety:'}</strong> {isDa ? 'Aloras Tryghedspakke' : 'Alora Safety Package'}</span>
                        </div>
                      </div>

                      {/* Short Description */}
                      <p className="text-stone-700 text-sm sm:text-base leading-relaxed mb-6 font-light">
                        {tripShortDesc}
                      </p>

                      {/* Expandable Extended Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden space-y-6 pt-2 pb-6 border-t border-stone-100"
                          >
                            {/* Full Description */}
                            {tripDesc && (
                              <div 
                                className="text-stone-600 text-sm leading-relaxed space-y-3 prose prose-stone"
                                dangerouslySetInnerHTML={{ __html: tripDesc }}
                              />
                            )}

                            {/* Highlights */}
                            {tripHighlights && (
                              <div>
                                <h3 className="text-sm font-serif font-bold text-[#99A18C] mb-2 italic">
                                  {isDa ? 'Højdepunkter & Tilgængelighed:' : 'Highlights & Accessibility:'}
                                </h3>
                                <div 
                                  className="text-xs sm:text-sm text-stone-600 space-y-1.5 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ul>li]:py-0.5"
                                  dangerouslySetInnerHTML={{ __html: tripHighlights }}
                                />
                              </div>
                            )}

                            {/* Alora Guarantee Box */}
                            <div className="bg-[#99A18C]/10 rounded-2xl p-4 border border-[#99A18C]/20">
                              <h3 className="text-xs font-bold text-[#99A18C] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                <CheckCircle2 size={14} />
                                {isDa ? 'Altid inkluderet hos Alora Rejser' : 'Always included with Alora Travel'}
                              </h3>
                              <p className="text-xs text-stone-700 leading-relaxed">
                                {isDa 
                                  ? 'Personlig rådgivning, forhåndsgodkendte handicapværelser, lufthavnsassistance, liftbus tur/retur og 24/7 dansk nødtelefon under rejsen.'
                                  : 'Personal consultation, pre-approved accessible rooms, airport assistance, accessible transfers, and 24/7 emergency support during your journey.'
                                }
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Card Action Footer */}
                  <div className="p-6 sm:p-8 pt-0 flex flex-col sm:flex-row items-center gap-3 border-t border-stone-100">
                    <button
                      onClick={() => toggleExpand(trip.id)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-xs font-bold text-[#99A18C] hover:text-[#8a917e] py-3 px-3 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
                    >
                      <span>{isExpanded ? (isDa ? 'Vis mindre' : 'Show less') : (isDa ? 'Læs mere' : 'Read more')}</span>
                      <ChevronDown size={15} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {hotelUrl && (
                      <Link
                        to={hotelUrl}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs sm:text-sm font-semibold py-3 px-4 rounded-xl transition-all"
                      >
                        <span>{isDa ? 'Se hotellet' : 'View hotel'}</span>
                      </Link>
                    )}

                    <Link
                      to={`${getLocalizedPath('quote', language)}?destination=${encodeURIComponent(tripTitle)}`}
                      className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-[#99A18C] hover:bg-[#8a917e] text-white text-xs sm:text-sm font-bold py-3.5 px-5 rounded-2xl shadow-md shadow-[#99A18C]/20 transition-all hover:scale-[1.02] text-center"
                    >
                      <span>{isDa ? 'Indhent tilbud' : 'Request quote'}</span>
                      <ArrowRight size={15} />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>
      )}

      {/* Custom Trip Callout */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">
        <div className="bg-stone-900 text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-xl">
          <div className="relative z-10 max-w-2xl mx-auto">
            <Plane className="mx-auto mb-4 text-[#99A18C]" size={36} />
            <h2 className="text-2xl md:text-4xl font-serif font-bold mb-4 italic">
              {isDa ? 'Har du en anden specifik rejsedrøm?' : 'Do you have another dream destination?'}
            </h2>
            <p className="text-stone-300 text-sm md:text-base leading-relaxed mb-8">
              {isDa
                ? 'Finder du ikke præcis den rejse, du havde i tankerne? Vi skræddersyr også gerne rejser til andre destinationer. Kontakt os og lad os tage en uforpligtende snak om dine ønsker og behov.'
                : 'Didn\'t find the exact journey you had in mind? We are delighted to tailor trips to other destinations as well. Contact us for an informal talk about your wishes and accessibility needs.'
              }
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={getLocalizedPath('contact', language)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#99A18C] hover:bg-[#8a917e] text-white font-bold py-3.5 px-8 rounded-2xl transition-all shadow-lg text-sm"
              >
                {isDa ? 'Kontakt Alora Rejser' : 'Contact Alora Rejser'}
              </Link>
              <a
                href="tel:40500780"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3.5 px-8 rounded-2xl transition-all text-sm"
              >
                <PhoneCall size={16} />
                {isDa ? 'Ring på 40 50 07 80' : 'Call +45 40 50 07 80'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
